import React, { useState, useEffect, useRef } from 'react';

interface LiveCounterProps {
    initialCount?: number;
    className?: string;
}

const LiveCounter: React.FC<LiveCounterProps> = ({ initialCount, className = '' }) => {
    // Vary base count by time of day to feel more realistic
    const getBaseCount = () => {
        const hour = new Date().getHours();
        // Peak hours: 9-11 and 14-17
        if (hour >= 9 && hour <= 11) return 14000 + Math.floor(Math.random() * 2000);
        if (hour >= 14 && hour <= 17) return 15000 + Math.floor(Math.random() * 3000);
        // Moderate hours: 7-9 and 17-21
        if ((hour >= 7 && hour < 9) || (hour > 17 && hour <= 21)) return 10000 + Math.floor(Math.random() * 2000);
        // Off hours: 22-6
        return 5000 + Math.floor(Math.random() * 2000);
    };

    const baseCount = useRef(initialCount || getBaseCount());
    const [count, setCount] = useState(0); // Start at 0 for the startup animation
    const [displayCount, setDisplayCount] = useState(0);
    const [isStarting, setIsStarting] = useState(true);

    // Startup animation: ramp up from 0 to base count
    useEffect(() => {
        const target = baseCount.current;
        const duration = 1500;
        const steps = 40;
        const stepValue = target / steps;
        let currentStep = 0;

        const startupTimer = setInterval(() => {
            currentStep++;
            if (currentStep >= steps) {
                setCount(target);
                setDisplayCount(target);
                setIsStarting(false);
                clearInterval(startupTimer);
            } else {
                const eased = Math.round(stepValue * currentStep * (currentStep / steps)); // ease-in
                setDisplayCount(Math.min(eased, target));
            }
        }, duration / steps);

        return () => clearInterval(startupTimer);
    }, []);

    // Regular fluctuation after startup
    useEffect(() => {
        if (isStarting) return;

        const interval = setInterval(() => {
            // Weighted random: more likely to stay close, occasional bigger jumps
            const rand = Math.random();
            let change: number;
            if (rand < 0.6) {
                change = Math.floor(Math.random() * 15) - 5; // small, slight upward bias
            } else if (rand < 0.9) {
                change = Math.floor(Math.random() * 50) - 20; // medium
            } else {
                change = Math.floor(Math.random() * 150) - 60; // occasional larger swing
            }
            setCount(prev => Math.max(baseCount.current * 0.7, prev + change));
        }, 2500 + Math.random() * 3000);

        return () => clearInterval(interval);
    }, [isStarting]);

    // Smooth display animation
    useEffect(() => {
        if (isStarting) return;

        const duration = 500;
        const steps = 20;
        const diff = count - displayCount;
        if (Math.abs(diff) < 2) {
            setDisplayCount(count);
            return;
        }
        const stepValue = diff / steps;
        let currentStep = 0;

        const animate = setInterval(() => {
            currentStep++;
            if (currentStep >= steps) {
                setDisplayCount(count);
                clearInterval(animate);
            } else {
                setDisplayCount(prev => Math.round(prev + stepValue));
            }
        }, duration / steps);

        return () => clearInterval(animate);
    }, [count, isStarting]);

    return (
        <div className={`flex items-center gap-2 ${className}`}>
            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            <span className="text-sm font-bold text-gray-600">
                <span className="text-gray-900">{displayCount.toLocaleString()}</span> active visitors
            </span>
        </div>
    );
};

export default LiveCounter;
